import type { Call } from '../types';
import { isReal } from './store';
import { callServer } from './server';

// Call recordings, doctors only.
// Real clinics: the server fetches the audio from Sarvam on demand and logs every play (functions/src/calling/recording.ts).
// Demo: sample recordings made from the synthetic transcripts with computer voices (frontend/public/demo-calls).
let demoList: Promise<Record<string, string>> | null = null;
const demoFiles = () =>
  (demoList ??= fetch('/demo-calls/manifest.json', { cache: 'no-cache' })
    .then((r) => (r.ok ? r.json() : { files: {} }))
    .then((m: { files?: Record<string, string> }): Record<string, string> => m.files ?? {})
    .catch((): Record<string, string> => ({})));

/** Demo reminder calls generated for late doses ("c-r…") share one sample recording. */
const demoKey = (callId: string) => (callId.startsWith('c-r') ? 'reminder' : callId);

export async function hasCallRecording(call: Call): Promise<boolean> {
  if (call.outcome === 'no_answer' || call.outcome === 'failed' || call.outcome === 'in_progress') return false;
  if (isReal()) return !!call.hasRecording;
  return !!(await demoFiles())[demoKey(call.id)];
}

/** Returns a playable URL. Real mode: each call to this is logged on the server as a play. */
export async function getCallRecordingUrl(call: Call): Promise<string> {
  if (isReal()) {
    const { audio, mime } = await callServer<{ audio: string; mime: string }>('getCallRecording', { facilityId: call.facilityId, callId: call.id });
    const bytes = Uint8Array.from(atob(audio), (c) => c.charCodeAt(0));
    return URL.createObjectURL(new Blob([bytes], { type: mime }));
  }
  const files = await demoFiles();
  const key = demoKey(call.id);
  if (!files[key]) throw new Error('NO_RECORDING');
  return `/demo-calls/${key}.mp3?v=${files[key]}`;
}

/** Demo only: a sample recording by key (e.g. a safety-review item "sr-1"), or null if none was made. */
export async function getDemoRecordingUrl(key: string): Promise<string | null> {
  if (isReal()) return null;
  const files = await demoFiles();
  return files[key] ? `/demo-calls/${key}.mp3?v=${files[key]}` : null;
}
